import { isAfter, isMatch, parse } from 'date-fns'
import { isValidPhoneNumber } from 'libphonenumber-js/max'
import { z, type ZodTypeAny } from 'zod'

import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import i18n from '@/i18n'
import type { Time } from '@/types/time'

export const requiredString = () =>
  z
    .string()
    .min(1, { message: i18n.t('error.required-field', { ns: 'common' }) })

export const requiredStringWithLabel = (label: string) =>
  z
    .string({
      required_error: i18n.t('error.required-field-with-label', {
        ns: 'common',
        LABEL: label
      })
    })
    .trim()
    .min(1, {
      message: i18n.t('error.required-field-with-label', {
        ns: 'common',
        LABEL: label
      })
    })

export const requiredArrayWithLabel = (label: string) =>
  z.array(z.string()).min(1, {
    message: i18n.t('error.required-field-with-label', {
      ns: 'common',
      LABEL: label
    })
  })

export const maxLength = (maxLenght: number) =>
  z.string().max(maxLenght, {
    message: i18n.t('error.max-length', { ns: 'common', NUMBER: maxLenght })
  })

export const email = (label: string) =>
  requiredStringWithLabel(label).email({
    message: i18n.t('error.incorrect-email-format', { ns: 'common' })
  })

export const date = () => z.coerce.date()

export const gender = () => z.union([z.literal('male'), z.literal('female')])

export const labelAndValue = <
  T extends z.ZodString | z.ZodNumber = z.ZodString
>(
  valueSchema?: T,
  message?: string
) =>
  z.object(
    {
      label: z.string(),
      value: (valueSchema || requiredString()) as T
    },
    {
      message: message || i18n.t('error.required-field', { ns: 'common' })
    }
  )

export const arrayOfOptions = z.array(labelAndValue())

export const userStatus = z.union([
  z.literal('active'),
  z.literal('inactive'),
  z.literal('blocked'),
  z.literal('archived')
])

export const teacherStatus = z.union([
  z.literal('busy'),
  z.literal('available'),
  z.literal('unavailable')
])

export const absenceStatus = z.union([
  z.literal('excused'),
  z.literal('unexcused')
])

export const attendanceStatus = z.union([
  z.literal('present'),
  z.literal('late'),
  z.literal('absent_excused'),
  z.literal('absent_unexcused'),
  z.literal('')
])

export const userProfile = z.union([
  z.literal('Superuser'),
  z.literal('Teacher'),
  z.literal('Tutor'),
  z.literal('Support'),
  z.literal('Planner')
])

export const userProfiles = z.array(userProfile)

export const phone = () =>
  z.string().refine(value => !value || isValidPhoneNumber(value), {
    message: i18n.t('error.incorrect-phone-format', { ns: 'common' })
  })

export const language = z.union([
  z.literal('de'),
  z.literal('en'),
  z.literal('fr')
])

export const commentType = z.union([
  z.literal('neutral'),
  z.literal('positive'),
  z.literal('negative')
])

export const semesterAndYearStatus = z.union([
  z.literal('active'),
  z.literal('archived'),
  z.literal('future')
])

export const time = () =>
  z.custom<Time>(
    value => isMatch(value, 'HH:mm'),
    i18n.t('error.incorrect-time-format', { ns: 'common' })
  )

export const timeRange = (message: string) =>
  z
    .object({ from: time().optional(), to: time().optional() })
    .refine(({ from, to }) => from && to, { message })
    .refine(
      range => {
        if (!range.from || !range.to) return false
        const from = parse(range.from, 'HH:mm', new Date())
        const to = parse(range.to, 'HH:mm', new Date())
        return isAfter(to, from)
      },
      {
        message: i18n.t('error.end-earlier-than-start', {
          ns: 'common'
        })
      }
    )

export const paginatedResults = <T extends ZodTypeAny>(result: T) =>
  z.object({
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(result),
    page_size: z.number(),
    current: z.number()
  })

export const order = () => z.union([z.literal('asc'), z.literal('desc')])

export const studentClass = z.object({
  id: requiredString().nullable(),
  display_name: requiredString()
})

export const classItem = z.object({
  id: requiredString(),
  display_name: requiredString()
})

export const idAndNameSchema = z.object({
  id: requiredString(),
  name: requiredString()
})

export const roomType = z.union([
  z.literal('regular'),
  z.literal('gym'),
  z.literal('lab'),
  z.literal('auditorium'),
  z.literal('music')
])

export const paginationSchema = z.object({
  page: z.number().catch(1),
  pageSize: z.number().catch(DEFAULT_PAGE_SIZE)
})

export const sortingSchema = z.object({
  sort: z.string().optional(),
  order: order().optional()
})

export const columnsSchema = z.object({
  hiddenColumns: z.array(z.string()).optional()
})

export const classAbsenceStatus = z.union([
  z.literal('excused'),
  z.literal('unexcused'),
  z.literal('pending'),
  z.literal('guardian_review'),
  z.literal('approved'),
  z.literal('rejected')
])

export const classAbsencePresence = z.union([
  z.literal('excused'),
  z.literal('unexcused')
])

export const absenceReasonUnion = z.union([
  z.literal('illness'),
  z.literal('medical_appointment'),
  z.literal('family_reason'),
  z.literal('religious'),
  z.literal('educational_activity'),
  z.literal('other')
])

export const semester = z.object({
  id: requiredString(),
  name: requiredString(),
  start_date: z.coerce.date(),
  end_date: z.coerce.date(),
  is_active: z.boolean(),
  school_year: requiredString(),
  status: semesterAndYearStatus
})
